import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { blogPosts } from "@/data/blog";
import { Clock, Calendar, ArrowRight } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com/" },
    { "@type": "ListItem", position: 2, name: "Blog", item: "https://tripsyfy.com/blog" },
  ],
});

export default function BlogIndexPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
      </Helmet>
      <SeoHead
        title="Tripsyfy Travel Blog | Flight Tips & Travel Guides"
        description="Practical travel guides from Tripsyfy — best times to book, group travel tips, cruise advice, and flight planning help."
        canonical="https://tripsyfy.com/blog"
      />
      <Navbar />

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <p className="overline text-accent mb-3">Travel Resources</p>
            <h1 className="heading-xl text-white mb-4">Tripsyfy Travel Blog</h1>
            <p className="text-white/70 text-lg max-w-2xl leading-relaxed">
              Practical travel guides — best times to book, group travel tips, cruise advice, and flight planning help from our team.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {blogPosts.map((post, i) => (
              <AnimatedSection key={post.slug} delay={i * 0.06}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 flex flex-col h-full overflow-hidden"
                >
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {post.readTime}
                      </span>
                    </div>
                    <h2 className="text-base font-bold text-primary mb-3 leading-snug group-hover:text-accent transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-accent">
                      Read article <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto text-center">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Ready to Book?</p>
            <h2 className="heading-lg text-white mb-4">Let our team handle your trip</h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Tell us where you want to go and we'll take care of flights, tours, or cruises — with real human support.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-accent text-white font-semibold px-7 py-3 rounded-lg text-sm hover:bg-accent/90 transition-colors"
            >
              Get a Free Quote
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
